<template>
  <nav
    class="bg-slate-700 flex flex-col justify-start z-40 pt-4 pb-4 px-3 xl:px-4 h-full transition-all duration-300 ease-in-out fixed xl:relative top-0 bottom-0 left-0 shadow-2xl xl:shadow-none select-none"
    :class="[
      modelValue ? 'translate-x-0' : '-translate-x-full xl:translate-x-0',
      modelValue ? 'w-64 xl:w-52' : 'w-64 xl:w-17'
    ]"
  >
    <div
      class="flex items-center gap-2"
      :class="modelValue ? 'justify-between xl:justify-end' : 'justify-center'"
    >
      <!-- 모바일 사이드바 사용자 이름 표시 (PC에서는 숨김) -->
      <div v-if="modelValue" class="flex items-center gap-2 xl:hidden ml-1 overflow-hidden">
        <div class="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
          <i class="fa-regular fa-user text-xs text-white"></i>
        </div>
        <span class="text-white font-bold text-sm truncate">
          {{ userStore.userName || '사용자' }} 님
        </span>
      </div>
      <button
        @click="toggleSidebar"
        class="p-1 rounded hover:bg-slate-600 transition-colors focus:outline-none"
        aria-label="사이드바 토글"
      >
        <UCIcon name="menu" customClass="bg-white w-7 h-7 xl:w-8 xl:h-8" />
      </button>
    </div>

    <hr class="mt-4 xl:mt-5 border-slate-600" />
    <ul
      class="flex-1 flex flex-col space-y-1.5 xl:space-y-2 bg-slate-700 mt-2 overflow-y-auto"
      style="font-family: '맑은 고딕', 'Malgun Gothic', sans-serif;"
    >
      <li v-for="menu in menus" :key="menu.path">
        <a
          @click="onMenuClick(menu)"
          class="flex items-center gap-2 px-2 py-2 hover:bg-slate-600 rounded text-base cursor-pointer transition-colors"
          :class="{
            'text-white font-bold bg-slate-800': tabStore.activePath === menu.path,
            'text-slate-300 font-normal': tabStore.activePath !== menu.path
          }"
        >
          <UCIcon
            :name="menu.icon"
            :customClass="tabStore.activePath === menu.path ? 'bg-white w-7 h-7 xl:w-8 xl:h-8' : 'bg-slate-400 w-7 h-7 xl:w-8 xl:h-8'"
          />
          <span v-if="modelValue" class="ml-2 xl:ml-3 whitespace-nowrap text-sm xl:text-base">
            {{ menu.name }}
          </span>
        </a>
      </li>
    </ul>

    <!-- 하단 로그아웃 버튼 -->
    <div class="mt-auto pt-3 border-t border-slate-600">
      <button
        type="button"
        @click="onLogoutClick"
        class="w-full flex items-center gap-2 px-2 py-2.5 text-slate-300 hover:text-white hover:bg-slate-600 rounded text-base cursor-pointer transition-colors focus:outline-none"
        :class="modelValue ? 'justify-start' : 'justify-center'"
        title="로그아웃"
      >
        <i class="fa-solid fa-right-from-bracket text-lg w-7 text-center text-slate-400"></i>
        <span v-if="modelValue" class="ml-2 whitespace-nowrap text-sm lg:text-base font-medium">
          로그아웃
        </span>
      </button>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, onMounted, watch, defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";
import { useTabStore } from "@/store/tabStore";
import { useUserStore } from "@/store/userStore";
import UCIcon from '@/components/UserControl/UCIcon.vue';

const router = useRouter();
const tabStore = useTabStore();
const userStore = useUserStore();

const props = defineProps({
  modelValue: Boolean,
});
const emit = defineEmits(["update:modelValue"]);

const toggleSidebar = () => {
  // console.log("toggleSidebar");
  emit("update:modelValue", !props.modelValue);
};

const sidebarClass = computed(
  () => (props.modelValue ? "translate-x-0 w-64" : "-translate-x-full w-0")
);

// 동적 메뉴 리스트 상태
const menus = ref([]);
const isLoadingMenu = ref(false);

/**
 * 사용자 아이디를 기반으로 메뉴 목록 로드
 */
async function loadMenus() {
  isLoadingMenu.value = true;
  try {
    const userId = userStore.userId;
    console.log(`[Sidebar] 사용자 아이디(${userId || '비로그인'})로 메뉴 조회 시작`);

    // userStore의 fetchUserMenu 호출 (내부적으로 목업 API getUserMenuList 실행)
    const list = await userStore.fetchUserMenu(userId);
    menus.value = list || [];
  } catch (error) {
    console.error("[Sidebar] 메뉴 로드 중 오류 발생:", error);
  } finally {
    isLoadingMenu.value = false;
  }
}

onMounted(() => {
  // 이미 store에 캐싱된 메뉴가 있으면 먼저 표시
  if (userStore.myMenu && userStore.myMenu.length > 0) {
    menus.value = [...userStore.myMenu];
  }
  // 최신 메뉴 조회
  loadMenus();
});

// 사용자 아이디 변경 감지 시 메뉴 다시 로드
watch(
  () => userStore.userId,
  (newUserId) => {
    if (newUserId) {
      loadMenus();
    }
  }
);

function onMenuClick(menu) {
  // console.log("onMenuClick menu router", menu, router);
  tabStore.addTab(menu);
  router.push(menu.path);

  // 모바일/가로모드(1280px 미만)에서는 메뉴 클릭 시 사이드바 자동 닫기
  if (window.innerWidth < 1280) {
    emit("update:modelValue", false);
  }
}

/**
 * 사이드바 하단 로그아웃 처리
 */
function onLogoutClick() {
  userStore.clearSession();
  if (window.innerWidth < 1280) {
    emit("update:modelValue", false);
  }
  router.push("/auth/login");
}
</script>
